/**
 * A view's assets (docs/views.md#2-data): files an agent or a page uploads, such as a dish's photo or a logo, kept by
 * their SHA-256 and served from the view's own address, `/api/views/<view>/assets/<hash>.<ext>`, so a record holds the
 * URL rather than the file. Coast cloud keeps them in R2 and the self-host on disk. Images, fonts, and 3D models: an SVG
 * could run script on the server's origin when opened by itself, so it isn't taken.
 */
export const ASSET_LIMITS = {
  /** One asset's bytes at most. */
  bytes: 5 << 20,
  /** A view's assets' bytes at most, all together: a repository's published view, such as Design, keeps its pictures here. */
  viewBytes: 200 << 20,
  /** How long fetching an asset from a URL may take, its redirects included. */
  fetchMs: 10_000,
  /** Redirects a fetch follows, at most. */
  redirects: 3,
} as const;

/** The images an asset may be, by the extension its URL ends in: a chat's photos are these alone. */
export const IMAGE_TYPES = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/avif": "avif",
} as const;
/**
 * The fonts an asset may be, since 2026-11-01: a design's own faces, which its page loads with `@font-face` from the
 * view's address, as the Design view's panels of a design's HTML are set in them.
 */
export const FONT_TYPES = {
  "font/woff2": "woff2",
  "font/woff": "woff",
  "font/ttf": "ttf",
  "font/otf": "otf",
} as const;
/** The 3D models an asset may be: a binary glTF, which the Scene template draws. */
export const MODEL_TYPES = { "model/gltf-binary": "glb" } as const;
/** The types an asset may have, by the extension its URL ends in. */
export const ASSET_TYPES = { ...IMAGE_TYPES, ...FONT_TYPES, ...MODEL_TYPES } as const;
export type AssetType = keyof typeof ASSET_TYPES;
export type ImageType = keyof typeof IMAGE_TYPES;
export const isAssetType = (value: unknown): value is AssetType => typeof value === "string" && value in ASSET_TYPES;

/** The path a view's asset is served at, on the view's server; with no file, the folder its page may load images from. */
export const assetPath = (view: string, file: string) => `/api/views/${view}/assets/${file}`;
/** Where every view's assets are served, which any view's page may load images from (src/client/renderer.ts). */
export const ASSETS_PATH = "/api/views/";

/** An asset's file name in its URL: its SHA-256 in hex and its type's extension. */
export const ASSET_FILE = /^([a-f0-9]{64})\.(png|jpg|webp|gif|avif|woff2|woff|ttf|otf|glb)$/;
export const assetFile = (hash: string, type: AssetType) => `${hash}.${ASSET_TYPES[type]}`;
export const typeOfFile = (file: string): AssetType | null => {
  const extension = ASSET_FILE.exec(file)?.[2];
  return (Object.entries(ASSET_TYPES).find(([, ext]) => ext === extension)?.[0] as AssetType | undefined) ?? null;
};

/**
 * Whether `url` is https on a host named on the internet: not an IP literal, `localhost`, or a local name such as
 * `printer.local`. The server fetches assets, and the self-host's render browser loads pages' requests, from the
 * server's own machine, which must not reach itself or its network for whoever asks.
 */
export function onTheInternet(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== "https:") return false;
  const host = parsed.hostname.toLowerCase().replace(/\.$/, "");
  const literal = /^[\d.]+$/.test(host) || host.startsWith("[");
  return !literal && host.includes(".") && !/(^|\.)(localhost|local|internal|lan|home|arpa)$/.test(host);
}

/** The type the bytes themselves say, from their first bytes, whatever a caller claims. */
export function sniffImageType(bytes: Uint8Array): ImageType | null {
  const starts = (...values: number[]) => values.every((value, index) => bytes[index] === value);
  const text = (offset: number, value: string) =>
    [...value].every((character, index) => bytes[offset + index] === character.charCodeAt(0));
  if (starts(0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)) return "image/png";
  if (starts(0xff, 0xd8, 0xff)) return "image/jpeg";
  if (text(0, "RIFF") && text(8, "WEBP")) return "image/webp";
  if (text(0, "GIF87a") || text(0, "GIF89a")) return "image/gif";
  if (text(4, "ftypavif") || text(4, "ftypavis")) return "image/avif";
  return null;
}
/** What an asset's bytes are, an image, a font, or a model, by how each kind's file starts; null for anything else. */
export function sniffAssetType(bytes: Uint8Array): AssetType | null {
  const text = (value: string) => [...value].every((character, index) => bytes[index] === character.charCodeAt(0));
  const image = sniffImageType(bytes);
  if (image) return image;
  if (text("wOF2")) return "font/woff2";
  if (text("wOFF")) return "font/woff";
  if (text("OTTO")) return "font/otf";
  if ((bytes[0] === 0 && bytes[1] === 1 && bytes[2] === 0 && bytes[3] === 0) || text("true")) return "font/ttf";
  if (text("glTF")) return "model/gltf-binary";
  return null;
}
